/**
 * useEdgeDrawing.ts — the "drag from a port to connect" concern, split out
 * of useCanvasGraph.ts to keep that hook from ballooning: live preview
 * bezier while dragging, nearest-input-port snapping, and committing a new
 * edge on mouseup over a valid target.
 */

import { useCallback, useEffect, useState } from "react";
import type { MutableRefObject } from "react";
import { getPortPoint, PORT_SNAP_DISTANCE } from "./canvasGeometry";
import type { Rect } from "./canvasGeometry";
import type { OutputPort, WorkflowEdge, WorkflowNode } from "../workflow.types";

export interface DrawingEdgeState {
  sourceNodeId: string;
  sourcePort: OutputPort;
  x: number;
  y: number;
}

interface UseEdgeDrawingOptions {
  nodesRef: MutableRefObject<WorkflowNode[]>;
  edgesRef: MutableRefObject<WorkflowEdge[]>;
  nodeRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  canvasPoint: (clientX: number, clientY: number) => { x: number; y: number };
  onCreateEdge: (edge: WorkflowEdge) => void;
}

let localEdgeIdCounter = 0;
function makeEdgeId() {
  localEdgeIdCounter += 1;
  return `edge-${Date.now()}-${localEdgeIdCounter}`;
}

export function useEdgeDrawing({
  nodesRef,
  edgesRef,
  nodeRefs,
  canvasPoint,
  onCreateEdge,
}: UseEdgeDrawingOptions) {
  const [drawingEdge, setDrawingEdge] = useState<DrawingEdgeState | null>(null);
  const [hoveredTargetId, setHoveredTargetId] = useState<string | null>(null);

  const nodeRect = useCallback(
    (nodeId: string): Rect | null => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      const el = nodeRefs.current[nodeId];
      if (!node || !el) return null;
      return { left: node.x, top: node.y, width: el.offsetWidth, height: el.offsetHeight };
    },
    [nodesRef, nodeRefs],
  );

  const findNearestInputPort = useCallback(
    (x: number, y: number, excludeNodeId: string) => {
      let best: { id: string; d: number } | null = null;
      nodesRef.current.forEach((n) => {
        if (n.id === excludeNodeId) return;
        const rect = nodeRect(n.id);
        if (!rect) return;
        const pt = getPortPoint(rect, "in");
        const d = Math.hypot(pt.x - x, pt.y - y);
        if (d <= PORT_SNAP_DISTANCE && (!best || d < best.d)) best = { id: n.id, d };
      });
      return best?.id ?? null;
    },
    [nodesRef, nodeRect],
  );

  const beginEdgeDraw = useCallback(
    (nodeId: string, port: OutputPort, clientX: number, clientY: number) => {
      // A node's output can fan out to any number of edges, and — since the
      // runner (WorkflowRunnerService.walkGraph) already tolerates a node
      // being reached from more than one branch without double-running it —
      // a node can just as validly fan IN from more than one branch
      // converging on it. onUp below only refuses an exact duplicate
      // connection, not a second distinct one.
      const p = canvasPoint(clientX, clientY);
      setDrawingEdge({ sourceNodeId: nodeId, sourcePort: port, x: p.x, y: p.y });
    },
    [canvasPoint],
  );

  useEffect(() => {
    if (!drawingEdge) return undefined;
    const onMove = (e: MouseEvent) => {
      const p = canvasPoint(e.clientX, e.clientY);
      setDrawingEdge((prev) => (prev ? { ...prev, x: p.x, y: p.y } : prev));
      setHoveredTargetId(findNearestInputPort(p.x, p.y, drawingEdge.sourceNodeId));
    };
    const onUp = (e: MouseEvent) => {
      const p = canvasPoint(e.clientX, e.clientY);
      const targetId = findNearestInputPort(p.x, p.y, drawingEdge.sourceNodeId);
      if (targetId) {
        // Only refuse an exact duplicate of an existing connection — the
        // same source+port already wired into this same target — not a
        // second, distinct incoming edge (that's a legitimate converge).
        const isDuplicate = edgesRef.current.some(
          (edge) =>
            edge.target === targetId &&
            edge.source === drawingEdge.sourceNodeId &&
            edge.sourcePort === drawingEdge.sourcePort,
        );
        if (!isDuplicate) {
          onCreateEdge({
            id: makeEdgeId(),
            source: drawingEdge.sourceNodeId,
            sourcePort: drawingEdge.sourcePort,
            target: targetId,
            targetPort: "in",
          });
        }
      }
      setDrawingEdge(null);
      setHoveredTargetId(null);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [drawingEdge, canvasPoint, findNearestInputPort, edgesRef, onCreateEdge]);

  return { drawingEdge, hoveredTargetId, beginEdgeDraw, nodeRect };
}
